import cx from 'clsx';
import type { ReactElement } from 'react';
import type { CheckboxProps as AriaCheckboxProps } from 'react-aria-components';
import { Checkbox as AriaCheckbox } from 'react-aria-components';
import { CheckmarkOutlinedCheckboxIcon } from '../checkbox/icons/checkmark-outlined-checkbox';
import { MinusOutlinedCheckboxIcon } from '../checkbox/icons/minus-outlined-checkbox';
import styles from './selector-checkbox.module.css';

export interface SelectorCheckboxProps extends AriaCheckboxProps {
  icon?: ReactElement;
  title?: string | ReactElement;
  subtitle?: string | ReactElement;
  additionalContent?: ReactElement;
  checkboxClassName?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}

export function SelectorCheckbox({
  isDisabled = false,
  isReadOnly = false,
  className,
  icon,
  title,
  subtitle,
  additionalContent,
  checkboxClassName,
  ...props
}: SelectorCheckboxProps): ReactElement {
  return (
    <AriaCheckbox
      className={cx(
        styles.selector,
        styles.classic,
        isReadOnly && styles.readonly,
        isDisabled && styles.disabled,
        className
      )}
      data-testid="selector-checkbox"
      isDisabled={isDisabled || isReadOnly}
      isReadOnly={isReadOnly}
      {...props}
    >
      {({ isSelected, isIndeterminate }) => (
        <>
          <div className={styles.contentWrapper}>
            {icon ? (
              <div className={styles.icon} data-testid="selector-icon">
                {icon}
              </div>
            ) : null}
            <div className={styles.header}>
              {title ? (
                <span className={cx('body-standard', styles.title)} data-testid="selector-title">
                  {title}
                </span>
              ) : null}
              {subtitle ? (
                <span
                  className={cx('body-standard', styles.subtitle)}
                  data-testid="selector-subtitle"
                >
                  {subtitle}
                </span>
              ) : null}
            </div>
            {additionalContent ? (
              <div
                className={cx('mr-8', styles.additionalContent)}
                data-testid="selector-additional-content"
              >
                {additionalContent}
              </div>
            ) : null}
          </div>
          <div className={cx('overlay', styles.checkbox, checkboxClassName)} data-testid="checkbox">
            {isSelected ? <CheckmarkOutlinedCheckboxIcon /> : null}
            {isIndeterminate ? <MinusOutlinedCheckboxIcon /> : null}
          </div>
        </>
      )}
    </AriaCheckbox>
  );
}
